import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ClubPublic {
  id: string;
  name: string;
  logo_url: string | null;
  city: string | null;
}

export const ClubCarousel = () => {
  const [clubs, setClubs] = useState<ClubPublic[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClubs();
  }, []);

  const loadClubs = async () => {
    try {
      const { data, error } = await supabase
        .from("clubs_public")
        .select("id, name, logo_url, city")
        .eq("is_active", true)
        .eq("has_active_subscription", true);

      if (error) throw error;

      const filtered = (data || []).filter(
        (c) =>
          c.name &&
          !c.name.toLowerCase().includes("demo") &&
          !c.name.toLowerCase().includes("test")
      );

      for (let i = filtered.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [filtered[i], filtered[j]] = [filtered[j], filtered[i]];
      }

      setClubs(filtered.slice(0, 5) as unknown as ClubPublic[]);
    } catch (error) {
      console.error("Error loading clubs for carousel:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clubs.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % clubs.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [clubs.length]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % clubs.length);
  }, [clubs.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + clubs.length) % clubs.length);
  }, [clubs.length]);

  const getVisibleClubs = () => {
    const visible: ClubPublic[] = [];
    for (let i = 0; i < Math.min(3, clubs.length); i++) {
      visible.push(clubs[(currentIndex + i) % clubs.length]);
    }
    return visible;
  };

  const visibleClubs = clubs.length > 0 ? getVisibleClubs() : [];

  return (
    <section className="py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <h3 className="text-lg font-semibold text-muted-foreground text-center mb-8">
          Deze tennis hussel app wordt o.a. gebruikt door:
        </h3>

        {loading ? (
          <div className="flex items-center justify-center gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className={`flex flex-col items-center gap-3 ${i === 1 ? "block" : "hidden md:flex"}`}>
                <div className="w-20 h-20 rounded-xl bg-muted animate-pulse" />
                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
              </div>
            ))}
          </div>
        ) : clubs.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground italic">
            Binnenkort zichtbaar — clubs worden geladen.
          </p>
        ) : (
          <>
            <div className="relative flex items-center justify-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={goPrev}
                className="shrink-0 text-muted-foreground hover:text-foreground"
                aria-label="Vorige"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>

              <div className="flex items-center justify-center gap-8 overflow-hidden">
                {visibleClubs.map((club, i) => (
                  <div
                    key={club.id}
                    className={`flex flex-col items-center gap-3 transition-opacity duration-500 ${
                      i === 0 ? "block" : "hidden md:flex"
                    }`}
                  >
                    {club.logo_url ? (
                      <div className="w-20 h-20 rounded-xl overflow-hidden bg-card border border-border flex items-center justify-center p-2">
                        <img
                          src={club.logo_url}
                          alt={club.name || "Club logo"}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-xl bg-card border border-border flex items-center justify-center">
                        <span className="text-2xl font-bold text-muted-foreground">
                          {club.name?.charAt(0) || "?"}
                        </span>
                      </div>
                    )}
                    <div className="text-center">
                      <p className="text-sm font-medium text-foreground">{club.name}</p>
                      {club.city && (
                        <p className="text-xs text-muted-foreground">{club.city}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={goNext}
                className="shrink-0 text-muted-foreground hover:text-foreground"
                aria-label="Volgende"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>

            <div className="flex items-center justify-center gap-2 mt-6">
              {clubs.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`h-2 w-2 rounded-full transition-colors ${
                    i === currentIndex ? "bg-accent" : "bg-muted-foreground/30"
                  }`}
                  aria-label={`Ga naar club ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};
